import axios from "axios";

const cleanBaseUrl = (value) => {
  if (!value) return "";
  return String(value).trim().replace(/\/+$/, "");
};

const API_BASE_URL =
  cleanBaseUrl(process.env.REACT_APP_API_URL) ||
  `${window.location.origin}/api`;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withRetry = async (requestFn, retries = 1) => {
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;
      const status = error?.response?.status;
      const shouldRetry =
        attempt < retries && (!status || status >= 500 || error.code === "ECONNABORTED");

      if (!shouldRetry) {
        throw error;
      }

      await sleep(350 * (attempt + 1));
    }
  }

  throw lastError;
};

const normalizeSuccess = (response) => ({
  success: Boolean(response?.data?.success),
  unavailableTimeSlots: response?.data?.unavailableTimeSlots || [],
  source: response?.data?.source || "api",
  message: response?.data?.message || "",
  addedTimeSlot: response?.data?.addedTimeSlot || null,
  raw: response?.data,
});

const normalizeError = (error, fallbackMessage) => {
  const message =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallbackMessage;

  return {
    success: false,
    error: message,
    unavailableTimeSlots: [],
    source: "error",
  };
};

export const getUnavailableTimeSlotsApi = async () => {
  try {
    const response = await withRetry(() => apiClient.get("/unavailable-time-slots"));
    return normalizeSuccess(response);
  } catch (error) {
    return {
      success: true,
      unavailableTimeSlots: [],
      source: "fallback_due_to_error",
      error:
        error?.response?.data?.error ||
        error?.message ||
        "Die Zeiten konnten nicht geladen werden.",
    };
  }
};

export const getUnavailableTimeSlotsForDateApi = async (date) => {
  try {
    const response = await withRetry(() =>
      apiClient.get(`/unavailable-time-slots/${date}`)
    );

    return {
      success: Boolean(response?.data?.success),
      unavailableTimeSlots: response?.data?.unavailableTimeSlots || [],
      source: response?.data?.source || "api",
      raw: response?.data,
    };
  } catch (error) {
    return normalizeError(error, "Die Zeiten fur dieses Datum konnten nicht geladen werden.");
  }
};

export const addUnavailableTimeSlotApi = async (date, startTime, endTime) => {
  try {
    const response = await withRetry(
      () =>
        apiClient.post("/unavailable-time-slots/add", {
          date,
          startTime,
          endTime,
        }),
      1
    );

    return normalizeSuccess(response);
  } catch (error) {
    return normalizeError(error, "Die Zeit konnte nicht hinzugefugt werden.");
  }
};

export const removeUnavailableTimeSlotApi = async (date, timeSlotId) => {
  try {
    const response = await withRetry(
      () =>
        apiClient.delete("/unavailable-time-slots/remove", {
          data: { date, timeSlotId },
        }),
      1
    );

    return normalizeSuccess(response);
  } catch (error) {
    return normalizeError(error, "Die Zeit konnte nicht entfernt werden.");
  }
};

export const removeAllUnavailableTimeSlotsForDateApi = async (date) => {
  try {
    const response = await withRetry(
      () =>
        apiClient.delete("/unavailable-time-slots/remove-all", {
          data: { date },
        }),
      1
    );

    return normalizeSuccess(response);
  } catch (error) {
    return normalizeError(error, "Die Zeiten konnten nicht entfernt werden.");
  }
};

